'use client';

import { useLandingTranslations } from './LandingLocaleProvider';
import { Reveal } from './Reveal';

function PlanStatusIcon() {
  return (
    <span className="relative flex h-5 w-5 items-center justify-center">
      <span className="absolute h-full w-full rounded-full bg-info/20" />
      <span className="relative h-2.5 w-2.5 rounded-full bg-info" />
    </span>
  );
}

function LinkIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5 text-base-content/40"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8c-1.657 0-3 1.567-3 3.5S10.343 15 12 15s3-1.567 3-3.5S13.657 8 12 8z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a7 7 0 1 0-14.8 0" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0 text-base-content/40"
      fill="none"
      viewBox="0 0 16 16"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m6 4 4 4-4 4" />
    </svg>
  );
}

function TaskStatusIcon() {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--chip-success-bg)] text-[var(--chip-success-fg)]">
      <svg aria-hidden="true" className="h-3 w-3" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m4 8 2.5 2.5L12 5" />
      </svg>
    </span>
  );
}

function TaskRow({
  title,
  wave,
  runner,
  dependency,
}: {
  title: string;
  wave: string;
  runner: string;
  dependency?: string;
}) {
  return (
    <li className="rounded-xl border-l-4 border-l-[var(--color-success)] bg-base-200/45 px-4 py-3">
      <div className="flex min-w-0 items-start gap-2">
        <span className="flex h-6 shrink-0 items-center">
          <TaskStatusIcon />
        </span>
        <span className="flex h-6 shrink-0 items-center">
          <ChevronIcon />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-base-content/75">{title}</p>
          {dependency ? (
            <div className="mt-1 flex min-w-0 items-center gap-1 text-[10px] text-base-content/45">
              <span>{dependency}</span>
            </div>
          ) : null}
        </div>
        <span className="flex h-6 shrink-0 items-center rounded-full bg-base-300 px-2 text-[10px] text-base-content/55">
          {wave}
        </span>
        <span className="flex h-6 shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 text-[10px] font-medium text-primary">
          <svg aria-hidden="true" className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z" />
          </svg>
          {runner}
        </span>
      </div>
    </li>
  );
}

function ExecutionPlanMockup({ t }: { t: (key: string) => string }) {
  return (
    <div className="mt-6 rounded-2xl border border-base-300 bg-base-100 p-5 md:p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h3 className="text-sm font-semibold">{t('whatYouGetExecutionTitle')}</h3>
        <span className="text-xs text-base-content/55">{t('whatYouGetExecutionProgress')}</span>
      </div>

      <div className="mb-5 h-2 w-full overflow-hidden rounded-full bg-base-300">
        <div className="h-full w-full rounded-full bg-[var(--color-success)]" />
      </div>

      <div className="mb-4 border-l-2 border-primary pl-4">
        <h4 className="mb-1 text-sm font-semibold text-primary">TL;DR</h4>
        <p className="text-sm leading-relaxed text-base-content/70">{t('whatYouGetExecutionTldr')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="rounded-full bg-base-200 px-2.5 py-1 text-[10px] text-base-content/55">
            {t('whatYouGetSourceDocs')}
          </span>
          <span className="rounded-full bg-base-200 px-2.5 py-1 text-[10px] text-base-content/55">
            {t('whatYouGetSourceConventions')}
          </span>
        </div>
      </div>

      <div className="mb-5 space-y-2">
        <div className="flex items-center gap-2 py-1">
          <ChevronIcon />
          <span className="text-sm font-semibold">{t('whatYouGetExecutionContext')}</span>
        </div>
        <div className="flex items-center gap-2 py-1">
          <ChevronIcon />
          <span className="text-sm font-semibold">{t('whatYouGetExecutionObjectives')}</span>
        </div>
      </div>

      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/50">
        {t('whatYouGetExecutionTasks')}
      </p>
      <ul className="space-y-2">
        <TaskRow title={t('whatYouGetExecutionTask1')} wave="W1" runner={t('whatYouGetExecutionRunner')} />
        <TaskRow title={t('whatYouGetExecutionTask2')} wave="W1" runner={t('whatYouGetExecutionRunner')} />
        <TaskRow
          title={t('whatYouGetExecutionTask3')}
          wave="W2"
          runner={t('whatYouGetExecutionRunner')}
          dependency={t('whatYouGetExecutionDependency')}
        />
      </ul>
    </div>
  );
}

function RelationColumn({
  heading,
  count,
  title,
  status,
  statusClassName,
  author,
  date,
  empty,
}: {
  heading: string;
  count: number;
  title?: string;
  status?: string;
  statusClassName?: string;
  author?: string;
  date?: string;
  empty?: string;
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl bg-base-200 px-3 py-2.5">
      <div className="mb-2 flex min-w-0 items-center gap-2">
        <h4 className="truncate text-sm font-semibold">{heading}</h4>
        <span className="rounded-full bg-base-300 px-2 py-0.5 text-xs font-medium text-base-content/50">{count}</span>
      </div>

      {title ? (
        <div className="min-w-0 rounded-md px-2 py-1">
          <p className="truncate text-sm font-medium">{title}</p>
          <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-base-content/50">
            <span className={`shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium ${statusClassName}`}>
              {status}
            </span>
            <span className="min-w-0 flex-1 truncate">{author}</span>
            <span className="shrink-0">{date}</span>
          </div>
        </div>
      ) : (
        <p className="px-2 py-1 text-sm text-base-content/40">{empty}</p>
      )}
    </div>
  );
}

export function WhatYouGetSection() {
  const t = useLandingTranslations();

  return (
    <Reveal>
      <section id="what-you-get" className="mt-16 scroll-mt-24">
        <div className="mb-8 text-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary">{t('whatYouGetEyebrow')}</p>
          <h2 className="text-2xl font-bold md:text-3xl">{t('whatYouGetTitle')}</h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-base-content/60">
            {t('whatYouGetSubtitle')}
          </p>
        </div>

        <div className="rounded-3xl border border-base-300 bg-base-200/40 p-3 shadow-sm md:p-5">
          <div className="overflow-hidden rounded-2xl border border-base-300 bg-base-100 shadow-xs">
            <div className="flex items-center justify-between gap-4 border-b border-base-300 bg-base-200/30 px-5 py-3">
              <div className="flex min-w-0 items-center gap-2 text-xs text-base-content/45">
                <span>{t('whatYouGetProjectName')}</span>
                <span>/</span>
                <span>{t('whatYouGetPlanBreadcrumb')}</span>
                <span>/</span>
                <span className="truncate font-medium text-base-content/70">{t('whatYouGetPlanTitle')}</span>
              </div>
              <span className="shrink-0 rounded-full border border-base-300 bg-base-100 px-2.5 py-1 text-[10px] font-medium text-base-content/50">
                {t('whatYouGetProductLabel')}
              </span>
            </div>

            <div className="p-5 md:p-8">
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <PlanStatusIcon />
                  <span className="inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
                    FEATURE
                  </span>
                  <span className="inline-flex items-center rounded-full bg-error/20 px-2 py-0.5 text-xs font-medium text-error">
                    HIGH
                  </span>
                </div>

                <h3 className="text-2xl font-bold tracking-tight md:text-3xl">{t('whatYouGetPlanTitle')}</h3>
                <p className="text-sm text-base-content/50">
                  {t('whatYouGetPlanMeta')}
                  <span className="mx-2 text-base-content/25">·</span>
                  {t('whatYouGetApproved')}
                </p>
              </div>

              <ExecutionPlanMockup t={t} />

              <div className="mt-6 min-w-0 rounded-2xl border border-base-300 bg-base-100 p-5 shadow-xs">
                <div className="mb-3 flex items-center gap-2">
                  <LinkIcon />
                  <h3 className="text-base font-semibold">{t('whatYouGetLinkedResults')}</h3>
                </div>

                <div className="grid min-w-0 gap-4 lg:grid-cols-3">
                  <RelationColumn
                    heading={t('whatYouGetArtifactReport')}
                    count={1}
                    title={t('whatYouGetReportItemTitle')}
                    status="COMPLETED"
                    statusClassName="bg-success/20 text-[var(--chip-success-fg)]"
                    author="Claude Code"
                    date={t('whatYouGetDate')}
                  />
                  <RelationColumn
                    heading={t('whatYouGetPostMortems')}
                    count={1}
                    title={t('whatYouGetPostMortemItemTitle')}
                    status={t('whatYouGetPostMortemStatus')}
                    statusClassName="bg-warning/20 text-[var(--chip-warning-fg)]"
                    author="Claude Code"
                    date={t('whatYouGetDate')}
                  />
                  <RelationColumn
                    heading={t('whatYouGetArtifactReview')}
                    count={1}
                    title={t('whatYouGetReviewItemTitle')}
                    status={t('whatYouGetReviewCompleted')}
                    statusClassName="bg-info/20 text-[var(--chip-info-fg)]"
                    author="Codex"
                    date={t('whatYouGetDate')}
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3 rounded-xl bg-primary/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-base-content/75">{t('whatYouGetEvidenceSummary')}</p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-base-content/50">
                  <span>{t('whatYouGetTracePlan')}</span>
                  <span>→</span>
                  <span>{t('whatYouGetTraceReview')}</span>
                  <span>→</span>
                  <span>{t('whatYouGetTraceHuman')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Reveal>
  );
}
